import type { ActivityType } from '../api/types';
import { sportOf } from './sports';
import { units, usesMiles } from '../stores/units.svelte';
import { decimal, integer, t } from '../i18n/index.ts';

// Formatting of activities, as the apps write it (shared ActivityFormat),
// with the words from i18n/common (spec 013 FR-010).

export function activityLabel(type: ActivityType): string {
  return sportOf(type).name;
}

export function activityIcon(type: ActivityType): string {
  return `la-${sportOf(type).icon}`;
}

// usesPace is true for sports shown as time per distance.
export function usesPace(type: ActivityType): boolean {
  return sportOf(type).pace;
}

// clockTime is "18:40" under an hour and "1:02:03" from an hour on.
export function clockTime(seconds: number): string {
  const s = Math.max(0, Math.round(seconds));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const pad = (n: number) => String(n).padStart(2, '0');
  return h > 0 ? `${h}:${pad(m)}:${pad(s % 60)}` : `${m}:${pad(s % 60)}`;
}

// length is "3.21 km" or "2.00 mi".
export function length(meters: number): string {
  const miles = usesMiles();
  const v = miles ? meters / 1609.344 : meters / 1000;
  return miles ? t('unitsDistanceMi', { distance: decimal(v, 2) }) : t('unitsDistanceKm', { distance: decimal(v, 2) });
}

// rate is pace ("5:48 /km") or speed ("24.3 km/h") over moving time.
export function rate(type: ActivityType, meters: number, movingS: number): string | null {
  if (meters < 10 || movingS <= 0) return null;
  const miles = usesMiles();
  const unit = miles ? 1609.344 : 1000;
  if (usesPace(type)) {
    const pace = clockTime((movingS * unit) / meters);
    return miles ? t('unitsPaceMi', { pace }) : t('unitsPaceKm', { pace });
  }
  const perHour = decimal((meters / unit) / (movingS / 3600), 1);
  return miles ? t('speedMph', { speed: perHour }) : t('speedKmh', { speed: perHour });
}

// climb is "38 m" or "125 ft".
export function climb(meters: number): string {
  return units().elevation === 'ft'
    ? t('unitsElevationFt', { elevation: integer(Math.round(meters * 3.28084)) })
    : t('unitsElevationM', { elevation: integer(Math.round(meters)) });
}

// speed formats a followed member's speed in the viewer's units (002 FR-020).
export function speed(mps: number): string {
  return usesMiles() ? t('speedMph', { speed: integer(Math.round(mps * 2.23694)) }) : t('speedKmh', { speed: integer(Math.round(mps * 3.6)) });
}

// MOVING_MPS is the speed from which a followed member's speed shows, so a
// badge does not flicker while they stand still.
export const MOVING_MPS = 1;

const doingIcons: Record<string, string> = {
  driving: 'las la-car',
  walking: 'las la-walking',
  running: 'las la-running',
  cycling: 'las la-biking',
  still: 'las la-pause',
};

// doing says what a member is doing from their report's activity; a phone
// that could not tell says Moving rather than a guess (002 FR-020).
export function doing(activity?: string): string {
  return t('activityDoing', { activity: activity && activity in doingIcons ? activity : 'other' });
}

export function doingIcon(activity?: string): string {
  return doingIcons[activity ?? ''] ?? 'las la-location-arrow';
}
